import React, { useState, useEffect } from 'react';
import { Grid, Container } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { createMuiTheme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';

import Header from '../../components/Header';
import StatisticsCard from '../../components/StatisticsCard';
import TimeEntryTable from '../../components/TimeEntryTable';
import NewEntry from '../../components/NewEntry';

import { firestore } from '../../utils/firebase';
import { useAuth } from '../../utils/useAuth';
import { generateReport } from '../../utils/api';
import {
  calculateHours,
  formatDate,
  formatTime,
} from '../../components/TimeEntryTable/TimeEntryTable';

const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#773CBD',
    },
    secondary: {
      main: '#773CBD',
    },
  },
});

const useStyles = makeStyles((theme) => ({
  mainContent: {
    marginTop: theme.spacing(20),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  marginOnMobile: {
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
}));

const Dashboard = () => {
  const [entryDialogOpen, setDialogOpen] = useState(false);
  const [userProfile, setUserProfile] = useState({
    isAdmin: false,
    firstName: '',
    lastName: '',
    email: '',
    initials: '—',
  });

  const { user } = useAuth();
  const classes = useStyles();

  const [statistics, setStatistics] = useState({ events: '–', hours: '–' });

  useEffect(() => {
    if (user.uid) {
      firestore
        .collection('users')
        .doc(user.uid)
        .get()
        .then((doc) => {
          const { isAdmin, firstName, lastName, email } = doc.data();
          const initials = `${firstName[0]}${lastName[0]}`.toUpperCase();

          setUserProfile({ firstName, lastName, email, isAdmin, initials });
        })
        .catch((e) => console.error(e));
    }
  }, [user.uid]);

  const updateStatistics = (entries) => {
    const distinctDates = [...new Set(entries.map((e) => e.date))];
    const totalMinutes = entries.reduce((sum, e) => sum + e.minutes, 0);

    setStatistics({
      events: distinctDates.length.toString(),
      hours: (totalMinutes / 60).toFixed(1),
    });
  };

  const handleReportGeneration = (startDate, endDate, closeDialog) => {
    const allRows = [
      [
        'First Name',
        'Last Name',
        'Email',
        'Date',
        'Event',
        'Start',
        'End',
        'Hours',
      ],
    ];

    firestore
      .collectionGroup('log')
      .get()
      .then((logSnapshot) => {
        logSnapshot.forEach((logDoc) => {
          const {
            firstName,
            lastName,
            email,
            date,
            event,
            start,
            end,
            minutes,
          } = logDoc.data();

          try {
            const formattedDate = formatDate(date);
            const formattedStart = formatTime(start);
            const formattedEnd = formatTime(end);
            const hours = calculateHours(minutes);

            allRows.push([
              firstName,
              lastName,
              email,
              formattedDate,
              event,
              formattedStart,
              formattedEnd,
              hours,
            ]);
          } catch (error) {
            console.log(logDoc);
            alert(
              `A time entry for ${firstName} ${lastName} is invalid and not included in the report.`
            );
          }
        });

        generateReport(
          allRows,
          { reportStart: startDate, reportEnd: endDate },
          closeDialog
        );
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <NewEntry
        onClose={() => setDialogOpen(false)}
        open={entryDialogOpen}
        userId={user.uid}
        firstName={userProfile.firstName}
        lastName={userProfile.lastName}
        email={userProfile.email}
      />
      <Header
        onReportGeneration={handleReportGeneration}
        initials={userProfile.initials}
        isAdmin={userProfile.isAdmin}
      />
      <Container maxWidth="lg" className={classes.mainContent}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} className={classes.marginOnMobile}>
            <StatisticsCard
              heading="Events Attended"
              stat={statistics.events}
              icon="today"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatisticsCard
              heading="Hours Logged"
              stat={statistics.hours}
              icon="access_time"
            />
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs={12} className={classes.tableContainer}>
            <TimeEntryTable
              userId={user.uid}
              handleNewEntry={() => setDialogOpen(true)}
              updateStatistics={updateStatistics}
            />
          </Grid>
        </Grid>
      </Container>
    </ThemeProvider>
  );
};

export default Dashboard;
